
import { Link, useSearchParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';

export default function News() {
  const [searchParams] = useSearchParams();
  const newsId = searchParams.get('id');

  // News data
  const newsArticles = {
    'great-zimbabwe-trip': {
      id: 'great-zimbabwe-trip',
      title: 'Educational Trip to Great Zimbabwe',
      date: '03 October 2025',
      image: 'https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/161832b976c33d6e8b2bafa028442993.jpeg',
      excerpt: 'Our students embarked on an incredible educational journey to the historic Great Zimbabwe ruins, exploring our rich cultural heritage and ancient civilization.',
      content: `
        <p>Elite College students recently embarked on an extraordinary educational journey to the historic Great Zimbabwe ruins, one of Africa's most significant archaeological sites. This immersive learning experience provided our students with a unique opportunity to explore Zimbabwe's rich cultural heritage and connect with the ancient civilization that once thrived in this region.</p>
        
        <p>The trip, organized by our History and Cultural Studies departments, was designed to bring classroom learning to life. Students had the chance to walk through the same stone corridors where the Shona people built their impressive city between the 11th and 15th centuries. The experience was both educational and deeply moving, as students gained firsthand insight into the sophisticated engineering and architectural skills of their ancestors.</p>
        
        <h3>Learning Beyond the Classroom</h3>
        <p>During the three-day expedition, students participated in guided tours led by expert archaeologists and local historians. They learned about the Great Enclosure, the Hill Complex, and the Valley Ruins, understanding how these structures served different purposes in the ancient city. The trip also included interactive workshops where students could examine artifacts and learn about traditional Shona culture.</p>
        
        <h3>Student Reflections</h3>
        <p>"Walking through Great Zimbabwe was like stepping back in time," said Tanyaradzwa Dube, a Form 1 student. "It made me proud of our heritage and inspired me to learn more about African history." Many students echoed similar sentiments, expressing how the trip deepened their appreciation for Zimbabwe's cultural legacy.</p>
        
        <h3>Educational Impact</h3>
        <p>The educational trip aligns with Elite College's commitment to holistic learning that extends beyond traditional classroom boundaries. By providing students with real-world experiences, we help them develop a deeper understanding of their cultural identity and historical context. This trip was particularly valuable for students studying history, archaeology, and cultural studies.</p>
        
        <p>The success of this educational expedition has encouraged the college to plan more such immersive learning experiences. Future trips may include visits to other significant historical sites across Zimbabwe and the broader African continent, continuing our mission to provide comprehensive, culturally-rich education to our students.</p>
      `
    }
  };

  const article = newsId ? newsArticles[newsId as keyof typeof newsArticles] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />

        <div className="max-w-4xl mx-auto px-4 py-16 pt-36 text-center">
          <h1 className="text-4xl font-bold text-[#007A3D] mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The news article you're looking for doesn't exist.</p>
          <Link to="/" className="bg-[#007A3D] hover:bg-[#C6A44C] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pt-36">
        {/* Back Button */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-[#007A3D] hover:text-[#C6A44C] cursor-pointer transition-colors">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>

        {/* Article Header */}
        <header className="mb-8">
          <div className="text-sm text-[#C6A44C] mb-4">{article.date}</div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#007A3D] mb-6">{article.title}</h1>
          <div className="w-full h-96 mb-8">
            <img 
              src={article.image} 
              alt={article.title} 
              className="w-full h-full object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </header>

        {/* Article Body */}
        <div className="prose prose-lg max-w-none">
          <div 
            className="text-gray-700 leading-relaxed space-y-6"
            dangerouslySetInnerHTML={{ __html: article.content }}
            style={{
              fontSize: '1.125rem',
              lineHeight: '1.75'
            }}
          />
        </div>

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-[#007A3D] mb-4">Share this article</h3>
          <div className="flex space-x-4">
            <button className="bg-[#007A3D] hover:bg-[#C6A44C] text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-facebook-fill mr-2"></i>
              Facebook
            </button>
            <button className="bg-[#007A3D] hover:bg-[#C6A44C] text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-twitter-fill mr-2"></i>
              Twitter
            </button>
            <button className="bg-[#007A3D] hover:bg-[#C6A44C] text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-linkedin-fill mr-2"></i>
              LinkedIn
            </button>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="text-2xl font-bold text-[#007A3D] mb-6">More News &amp; Events</h3>
          <div className="bg-gray-50 p-6 rounded-lg text-center">
            <p className="text-gray-600 mb-4">Stay tuned for more exciting news and events from Elite College!</p>
            <Link to="/" className="bg-[#007A3D] hover:bg-[#C6A44C] text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              Back to Homepage
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-[#005A2C] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://static.readdy.ai/image/e999ec2dea4e4b618de520757ef9db5d/995999b745e34f0ed4248820e32af9bb.jpeg" 
                  alt="Elite College Logo" 
                  className="h-10 w-10 object-cover rounded-full"
                />
                <h3 className="text-xl font-bold ml-3">Elite College</h3>
              </div>
              <p className="text-gray-300 mb-4">Empowering tomorrow's leaders through excellence in education and holistic development.</p>
              <div className="flex space-x-4">
                <button className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </button>
                <button className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-twitter-fill text-xl"></i>
                </button>
                <button className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </button>
                <button className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </button>
                <a href="https://wa.me/263774872973" target="_blank" rel="noopener noreferrer" className="text-[#C6A44C] hover:text-white cursor-pointer">
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors">About Us</Link></li>
                <li><Link to="/admissions" className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors">Admissions</Link></li>
                <li><Link to="/gallery" className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors">Gallery</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-[#C6A44C] cursor-pointer transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center">
                  <i className="ri-map-pin-line mr-2 text-[#C6A44C]"></i>
                  535 Mataga
                </li>
                <li className="flex items-center">
                  <i className="ri-phone-line mr-2 text-[#C6A44C]"></i>
                  +263 774 872 973
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2 text-[#C6A44C]"></i>
                  info@elitecollege.co.zw
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Office Hours</h4>
              <ul className="space-y-2 text-gray-300">
                <li>Monday - Thursday: 7:30 AM - 4:00 PM</li>
                <li>Friday: 7:30 AM - 3:00 PM</li>
                <li>Break: 10:00 AM - 10:30 AM</li>
                <li>Lunch: 1:00 PM - 2:00 PM</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-[#007A3D] mt-8 pt-8 text-center text-gray-300">
            <p>&copy; 2026 Elite College. All rights reserved.</p>
            <p className="mt-2 text-sm">Powered by <a href="https://onhightech.co.zw" target="_blank" rel="noopener noreferrer" className="text-[#C6A44C] hover:text-white underline cursor-pointer transition-colors">Onhightech Solutions</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
